"use client"
import { useState, useEffect } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card"
import { Button } from "../ui/button"
import { Edit, LogOut, Plus } from "lucide-react"
import { useAuth } from '../../context/AuthContext'
import { API_BASE_URL } from '../../services/api'

export default function DashboardUser() {
  const { user, logout } = useAuth()

  // Static user data
  const UserData = {
    name: `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || null,
    email: user?.email || null,
    avatar: user?.photo || null,
    joinedDate: user?.createdAt || null,
  };

  // Create a utility function for image URLs
  const getImageUrl = (imagePath: string | null | undefined): string => {
    if (!imagePath || imagePath === "/placeholder.svg") {
      return "/placeholder.svg";
    }

    // If it's already a full URL, return as is
    if (imagePath.startsWith('http')) {
      return imagePath;
    }

    // If it's a relative path, prepend the API base URL
    return `${API_BASE_URL}${imagePath}`;
  };

  const handleLogout = async () => {
    await logout()
    window.location.href = '/signin'
  }

  // Format date for display
  const formatDate = (date: string | Date | null) => {
    if (!date) return 'Jamais';

    try {
      const parsedDate = typeof date === 'string' ? new Date(date) : date;
      return parsedDate.toLocaleDateString("fr-FR");
    } catch {
      return 'Date invalide';
    }
  };

  return (
    <div className="flex-1">
      <div className="container px-4 py-8 md:px-6 md:py-12">
        <div className="flex flex-col gap-8">
          <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
            <div>
              <h1 className="text-3xl font-serif font-bold tracking-tight">Tableau de bord</h1>
              <p className="text-gray-500 dark:text-gray-400">
                Bienvenue, {UserData.name}. Gérez vos informations et créez vos formations.
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleLogout} variant="outline" size="sm">
                <LogOut className="mr-2 h-4 w-4" />
                Déconnexion
              </Button>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid gap-6 md:grid-cols-2">
            {/* Personal Information Section */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle>Informations personnelles</CardTitle>
                <CardDescription>Vos informations de profil</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col md:flex-row gap-6 items-center md:items-start">
                  <div className="relative w-32 h-32 flex-shrink-0">
                    {UserData.avatar && UserData.avatar !== "/placeholder.svg" ? (
                      <img
                        src={getImageUrl(UserData.avatar)}
                        alt={UserData.name || undefined}
                        className="rounded-full object-cover border-4 border-white shadow-md w-full h-full absolute inset-0"
                        onError={(e) => {
                          // Fallback to placeholder if image fails to load
                          e.currentTarget.src = "/placeholder.svg";
                        }}
                      />
                    ) : (
                      <div className="flex items-center justify-center rounded-full bg-gray-300 text-white text-4xl font-bold border-4 border-white shadow-md w-full h-full absolute inset-0">
                        {UserData.name?.charAt(0).toUpperCase() || "U"}
                      </div>
                    )}
                  </div>
                  <div className="space-y-4 text-center md:text-left">
                    <div>
                      <h3 className="text-xl font-bold">{UserData.name}</h3>
                      <p className="text-gray-500 dark:text-gray-400">{UserData.email}</p>
                    </div>
                    <div className="flex flex-col gap-1">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Membre depuis {formatDate(UserData.joinedDate)}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Formateur
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions Section */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle>Actions rapides</CardTitle>
                <CardDescription>Gérez votre compte et vos formations</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex flex-col gap-3">
                    <Button variant="default" asChild className="w-fit">
                      <a href="/formateur/courses/new">
                        <Plus className="mr-2 h-4 w-4" />
                        Créer une formation
                      </a>
                    </Button>

                    <Button variant="outline" asChild className="w-fit">
                      <a href="/formateur/profile">
                        <Edit className="mr-2 h-4 w-4" />
                        Modifier le profil
                      </a>
                    </Button>
                  </div>

                  <div className="space-y-2 text-sm text-gray-500 dark:text-gray-400">
                    <p>• Créez et gérez vos formations</p>
                    <p>• Mettez à jour vos informations personnelles</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}
